import { useQueueClaimConflict } from '@/hooks/useQueueClaimConflict';
import { ReviewPackageActions } from '@/types/ReviewPackage';
import { Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { QueueRejectionReason, QueueStatus, QueueType } from 'types';

import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery, useUpdateQueueMutation } from '../../services/queues';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { DocumentRejectionModal } from '../DocumentRejectionModal/DocumentRejectionModal';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';

export const PTOProofReview = ({ isPTODateSubmitting }: { isPTODateSubmitting: boolean }) => {
  const { claims, user } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { id = '', action = ReviewPackageActions.review } = useParams<{
    id: string;
    action: ReviewPackageActions;
  }>();
  const { data: permissionToOperateQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.permissionToOperate,
  });
  const [updateQueue, { isLoading: isUpdatingQueue }] = useUpdateQueueMutation();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const allowsAction = useIsOrgReadOnly();
  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(permissionToOperateQueue);

  const handleQueueUpdate = async (status: QueueStatus, note?: string, reasons?: QueueRejectionReason[]) => {
    try {
      if (permissionToOperateQueue?.id) {
        await updateQueue({
          id: permissionToOperateQueue.id,
          data: {
            assigneeId: user?.sub,
            status,
            ...(reasons && {
              rejectionReasons: [
                {
                  reasons,
                  ...(note && { note }),
                },
              ],
            }),
          },
        }).unwrap();
        toast.success('Queue updated successfully');
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating queue: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating queue');
      }
    }
  };

  const approve = () => checkClaimAndProceed(() => handleQueueUpdate(QueueStatus.approved));

  const handleRejectClick = () => checkClaimAndProceed(openModal);

  const handleRejectModal = async (values: { rejectionReason: string; notes: string | undefined }) => {
    const { rejectionReason, notes } = values;
    await handleQueueUpdate(QueueStatus.rejected, notes, [rejectionReason as QueueRejectionReason]);
    closeModal();
  };

  return permissionToOperateQueue && (permissionToOperateQueue.status === QueueStatus.rejected || isFinCoUser) ? (
    <>
      {claimConflictModal}
      <DocumentRejectionModal
        isModalOpen={isModalOpen}
        title="Permission to Operate"
        handleClose={closeModal}
        handleSubmit={handleRejectModal}
      />
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.permissionToOperate} />
      <Box padding="md" margin="0 0 lg 0" borderColor="separator" borderWidth="xs" gap="sm">
        <h3>Permission to Operate Review</h3>
        <QueueBanner
          setIsNotesOpen={setIsNotesOpen}
          queueType={QueueType.permissionToOperate}
          isFinCoUser={isFinCoUser}
          hideFlagCount
        />
        {action === ReviewPackageActions.review &&
          permissionToOperateQueue.status !== QueueStatus.approved &&
          permissionToOperateQueue.status !== QueueStatus.completeHoldForApproval && (
            <Box direction="row" childGap="xs" justifyContent="flex-end" alignItems="flex-end">
              <Button
                size="sm"
                variant="primary"
                isLoading={isUpdatingQueue}
                onClick={approve}
                disabled={!allowsAction || isPTODateSubmitting}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="primary"
                tone="danger"
                isLoading={isUpdatingQueue}
                onClick={handleRejectClick}
                disabled={!allowsAction || isPTODateSubmitting}
              >
                Reject
              </Button>
            </Box>
          )}
      </Box>
    </>
  ) : null;
};
